import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { 
  Sparkles, 
  RefreshCw, 
  Plus, 
  Edit, 
  Settings,
  Search,
  Filter,
  X,
  Loader2,
  Zap,
  Shield,
  Database,
  Code,
  BarChart3,
  Users,
  Mail,
  Bell,
  FileText,
  Lock,
  Globe,
  Cloud,
  Cpu,
  Network,
  HardDrive,
  Server,
  Terminal,
  Palette,
  Image,
  Video,
  Music,
  File,
  Folder,
  Star,
  Heart,
  Award,
  Target,
  TrendingUp,
  Activity,
  PieChart,
  LineChart,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  HelpCircle,
  Key,
  Circle
} from 'lucide-react';
import { apiRequest } from '../../services/apiClient';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';

/**
 * Master Features Management Screen
 * Full page for managing global master features
 */
const MasterFeaturesManagement = () => {
  const [features, setFeatures] = useState([]);
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [formData, setFormData] = useState({
    featureName: '',
    description: '',
    isActive: true,
    tenantId: null // Global features - tenantId can be null or a default value
  });

  useEffect(() => {
    loadFeatures();
  }, []);

  useEffect(() => {
    filterFeatures();
  }, [features, searchQuery, statusFilter]);

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const response = await apiRequest(
        '/gateway/api/ticket/master-features/all',
        { method: 'GET' }
      );

      //console.log('📦 All features response:', response);

      // Handle response structure
      let featuresData = [];
      if (Array.isArray(response)) {
        featuresData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        featuresData = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        featuresData = response.data.data;
      }

      setFeatures(featuresData);
    } catch (error) {
      console.error('Error loading features:', error);
      toast.error(error.response?.data?.message || 'Failed to load master features');
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  };

  const filterFeatures = () => {
    let filtered = [...features];

    // Apply search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(f => {
        const name = (f.featureName || f.feature_name || '').toLowerCase();
        const desc = (f.description || '').toLowerCase();
        return name.includes(searchLower) || desc.includes(searchLower);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(f => {
        const isActive = f.isActive !== undefined ? f.isActive : (f.is_active !== undefined ? f.is_active : false);
        return statusFilter === 'active' ? isActive : !isActive;
      });
    }

    setFilteredFeatures(filtered);
  };

  const handleCreate = () => {
    setEditingFeature(null);
    setFormData({
      featureName: '',
      description: '',
      isActive: true,
      tenantId: null
    });
    setShowCreateModal(true);
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setFormData({
      featureName: feature.featureName || feature.feature_name || '',
      description: feature.description || '',
      isActive: feature.isActive !== undefined ? feature.isActive : (feature.is_active !== undefined ? feature.is_active : true),
      tenantId: feature.tenantId || feature.tenant_id || null
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.featureName || formData.featureName.trim() === '') {
      toast.error('Feature name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        featureName: formData.featureName.trim(),
        description: formData.description || '',
        tenantId: formData.tenantId || 1, // Default to tenant 1 for global features
        isActive: formData.isActive !== undefined ? formData.isActive : true
      };

      let response;
      if (editingFeature) {
        // Update existing feature
        const featureId = editingFeature.id || editingFeature.feature_id;
        response = await apiRequest(
          `/gateway/api/ticket/master-features/${featureId}`,
          {
            method: 'PUT',
            body: payload
          }
        );
        toast.success('Feature updated successfully');
      } else {
        // Create new feature
        response = await apiRequest(
          '/gateway/api/ticket/master-features/save',
          {
            method: 'POST',
            body: payload
          }
        );
        toast.success('Feature created successfully');
      }

      // Soft patch: Update local state immediately
      if (editingFeature) {
        setFeatures(prev => prev.map(f => {
          const featureId = editingFeature.id || editingFeature.feature_id;
          const currentId = f.id || f.feature_id;
          if (currentId === featureId) {
            return {
              ...f,
              featureName: payload.featureName,
              description: payload.description,
              isActive: payload.isActive
            };
          }
          return f;
        }));
      } else {
        // Add new feature to the list
        const newFeature = response?.data || response;
        if (newFeature) {
          setFeatures(prev => [...prev, newFeature]);
        } else {
          // If response doesn't include the new feature, refetch
          await loadFeatures();
        }
      }

      setShowCreateModal(false);
      setEditingFeature(null);
    } catch (error) {
      console.error('Error saving feature:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save feature';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (feature) => {
    const featureId = feature.id || feature.feature_id;
    const newActiveState = !(feature.isActive !== undefined ? feature.isActive : feature.is_active);

    try {
      const payload = {
        featureName: feature.featureName || feature.feature_name || '',
        description: feature.description || '',
        tenantId: feature.tenantId || feature.tenant_id || 1,
        isActive: newActiveState
      };

      await apiRequest(
        `/gateway/api/ticket/master-features/${featureId}`,
        {
          method: 'PUT',
          body: payload
        }
      );

      // Soft patch: Update local state immediately
      setFeatures(prev => prev.map(f => {
        const currentId = f.id || f.feature_id;
        if (currentId === featureId) {
          return {
            ...f,
            isActive: newActiveState
          };
        }
        return f;
      }));

      toast.success(`Feature ${newActiveState ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error(error.response?.data?.message || 'Failed to update feature status');
    }
  };

  const handleDelete = async (feature) => {
    if (!window.confirm(`Are you sure you want to delete "${feature.featureName || feature.feature_name}"?`)) {
      return;
    }

    const featureId = feature.id || feature.feature_id;

    try {
      await apiRequest(
        `/gateway/api/ticket/master-features/${featureId}`,
        { method: 'DELETE' }
      );

      // Soft patch: Remove from local state immediately
      setFeatures(prev => prev.filter(f => {
        const currentId = f.id || f.feature_id;
        return currentId !== featureId;
      }));

      toast.success('Feature deleted successfully');
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error(error.response?.data?.message || 'Failed to delete feature');
    }
  };

  // Icon mapping for unique feature icons
  const getFeatureIcon = (featureName) => {
    const name = (featureName || '').toLowerCase();
    
    // Category-based icon assignment
    if (name.includes('api') || name.includes('integration')) return Code;
    if (name.includes('security') || name.includes('auth') || name.includes('permission')) return Shield;
    if (name.includes('database') || name.includes('data')) return Database;
    if (name.includes('analytics') || name.includes('report') || name.includes('metric')) return BarChart3;
    if (name.includes('user') || name.includes('agent') || name.includes('team')) return Users;
    if (name.includes('email') || name.includes('mail')) return Mail;
    if (name.includes('notification') || name.includes('alert') || name.includes('bell')) return Bell;
    if (name.includes('document') || name.includes('file') || name.includes('pdf')) return FileText;
    if (name.includes('lock') || name.includes('access')) return Lock;
    if (name.includes('global') || name.includes('world')) return Globe;
    if (name.includes('cloud') || name.includes('storage')) return Cloud;
    if (name.includes('cpu') || name.includes('performance')) return Cpu;
    if (name.includes('network') || name.includes('connection')) return Network;
    if (name.includes('server') || name.includes('host')) return Server;
    if (name.includes('terminal') || name.includes('command')) return Terminal;
    if (name.includes('color') || name.includes('theme') || name.includes('style')) return Palette;
    if (name.includes('image') || name.includes('photo') || name.includes('picture')) return Image;
    if (name.includes('video') || name.includes('media')) return Video;
    if (name.includes('music') || name.includes('audio')) return Music;
    if (name.includes('folder') || name.includes('directory')) return Folder;
    if (name.includes('star') || name.includes('favorite')) return Star;
    if (name.includes('heart') || name.includes('like')) return Heart;
    if (name.includes('award') || name.includes('achievement')) return Award;
    if (name.includes('target') || name.includes('goal')) return Target;
    if (name.includes('trend') || name.includes('growth')) return TrendingUp;
    if (name.includes('activity') || name.includes('log')) return Activity;
    if (name.includes('chart') || name.includes('graph')) return PieChart;
    if (name.includes('calendar') || name.includes('schedule')) return Calendar;
    if (name.includes('time') || name.includes('clock')) return Clock;
    if (name.includes('check') || name.includes('complete')) return CheckCircle;
    if (name.includes('alert') || name.includes('warning')) return AlertCircle;
    if (name.includes('info') || name.includes('information')) return Info;
    if (name.includes('help') || name.includes('support')) return HelpCircle;
    
    // Default icons based on first letter or common patterns
    if (name.startsWith('a')) return Award;
    if (name.startsWith('b')) return BarChart3;
    if (name.startsWith('c')) return Code;
    if (name.startsWith('d')) return Database;
    if (name.startsWith('e')) return Mail;
    if (name.startsWith('f')) return FileText;
    if (name.startsWith('g')) return Globe;
    if (name.startsWith('h')) return HardDrive;
    if (name.startsWith('i')) return Info;
    if (name.startsWith('j')) return Zap;
    if (name.startsWith('k')) return Key;
    if (name.startsWith('l')) return Lock;
    if (name.startsWith('m')) return Music;
    if (name.startsWith('n')) return Network;
    if (name.startsWith('o')) return Circle;
    if (name.startsWith('p')) return PieChart;
    if (name.startsWith('q')) return HelpCircle;
    if (name.startsWith('r')) return RefreshCw;
    if (name.startsWith('s')) return Shield;
    if (name.startsWith('t')) return Target;
    if (name.startsWith('u')) return Users;
    if (name.startsWith('v')) return Video;
    if (name.startsWith('w')) return Globe;
    if (name.startsWith('x')) return X;
    if (name.startsWith('y')) return Star;
    if (name.startsWith('z')) return Zap;
    
    // Default to Sparkles icon
    return Sparkles;
  };

  const statusFilterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Master Features Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Configure and manage global platform features.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={loadFeatures}
            disabled={loading}
            className="flex items-center gap-2 border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-[#006600] hover:bg-[#005500] text-white border-[#006600]"
          >
            <Sparkles className="h-4 w-4" />
            Create Feature
          </Button>
        </div>
      </div>

      {/* Features Table */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Master Features ({filteredFeatures.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search & Filter Bar - Compact, side-by-side */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search features by name or description..."
                    className="pl-10 w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <StatusFilterDropdown
                  value={statusFilter}
                  options={statusFilterOptions}
                  placeholder="Filter by Status"
                  onChange={setStatusFilter}
                  icon={Filter}
                />
              </div>
            </div>
            {filteredFeatures.length === 0 && !loading ? (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No features found</p>
                <p className="text-sm text-gray-500 mt-1">
                  {searchQuery || statusFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'No features available'}
                </p>
                {(searchQuery || statusFilter !== 'all') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchQuery('');
                    }}
                    className="mt-4 border-[#006600] text-[#006600] hover:bg-green-50"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">S.No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Feature Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Active Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeatures.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          No features found. Click "Create Feature" to add a new feature.
                        </td>
                      </tr>
                    ) : (
                      filteredFeatures.map((feature, index) => {
                        const featureId = feature.id || feature.feature_id;
                        const featureName = feature.featureName || feature.feature_name || 'Unknown Feature';
                        const description = feature.description || '';
                        const isActive = feature.isActive !== undefined ? feature.isActive : (feature.is_active !== undefined ? feature.is_active : false);
                        const FeatureIcon = getFeatureIcon(featureName);

                        return (
                          <tr 
                            key={featureId} 
                            className="border-b border-gray-200 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <FeatureIcon className="h-4 w-4 text-blue-600" />
                                </div>
                                <span>{featureName}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {description || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge 
                                variant="outline" 
                                className={isActive 
                                  ? "bg-green-50 text-green-700 border-green-200" 
                                  : "bg-red-50 text-red-700 border-red-200"
                                }
                              >
                                {isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(feature)}
                                  className="border-[#006600] text-[#006600] hover:bg-green-50"
                                  title="Edit Feature"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleActive(feature)}
                                  className={isActive 
                                    ? "border-orange-500 text-orange-600 hover:bg-orange-50" 
                                    : "border-green-500 text-green-600 hover:bg-green-50"
                                  }
                                  title={isActive ? "Deactivate Feature" : "Activate Feature"}
                                >
                                  <Settings className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingFeature(null);
        }}
        title={editingFeature ? 'Edit Feature' : 'Create New Feature'}
        size="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setEditingFeature(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#006600] hover:bg-[#005500] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {editingFeature ? 'Update Feature' : 'Create Feature'}
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Feature Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.featureName}
              onChange={(e) => setFormData(prev => ({ ...prev, featureName: e.target.value }))}
              placeholder="Enter feature name"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter feature description"
              rows={4}
              className="w-full"
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
            />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MasterFeaturesManagement;
